import { Link, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { FilterChips } from './FilterChips.tsx';
import { FilterSidebar } from './FilterSidebar.tsx';
import { Icon } from './Icon.tsx';
import { CommandPalette } from './CommandPalette.tsx';
import { AccessBadge } from './AccessBadge.tsx';
import { HelpPanel } from './HelpPanel.tsx';
import { loadFiltersFromUrl } from '../stores/filter-store.ts';

interface Props {
  user: { display_name: string; role: string } | null;
  children: React.ReactNode;
}

export function AppShell({ user, children }: Props) {
  const loc = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [dark, setDark] = useState(false);

  useEffect(() => {
    loadFiltersFromUrl();
    // Theme aus System-Pref oder localStorage
    const stored = localStorage.getItem('stat:theme');
    const isDark =
      stored === 'dark' ||
      (!stored && window.matchMedia('(prefers-color-scheme: dark)').matches);
    setDark(isDark);
    document.documentElement.classList.toggle('dark', isDark);
  }, []);

  function toggleTheme() {
    const next = !dark;
    setDark(next);
    localStorage.setItem('stat:theme', next ? 'dark' : 'light');
    document.documentElement.classList.toggle('dark', next);
  }

  return (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-slate-950">
      {/* Konsolen-Leiste: dunkel in BEIDEN Themes — der Rahmen des Instruments.
          Mix-Markt-CI sitzt in der Wortmarke (blau/rotes Zeichen). */}
      <header className="bg-slate-900 dark:bg-slate-950 border-b border-slate-800 px-4 h-14 flex items-center gap-3 shrink-0 sticky top-0 z-30">
        <a
          href="/"
          className="flex items-center gap-1.5 text-sm font-medium text-slate-400 hover:text-white transition-colors group"
          title="Zurück zum DRS-Cockpit"
        >
          <Icon name="chevronRight" size={14} className="rotate-180 group-hover:-translate-x-0.5 transition-transform" />
          Cockpit
        </a>
        <div className="w-px h-5 bg-slate-700" />
        <div className="flex items-center gap-2.5">
          <div className="relative w-7 h-7 rounded-lg bg-brand-600 text-white flex items-center justify-center overflow-hidden ring-1 ring-white/15">
            <Icon name="trendingUp" size={15} />
            <div className="absolute bottom-0 inset-x-0 h-[3px] bg-accent-600" />
          </div>
          <div className="leading-none">
            <h1 className="text-base font-semibold tracking-tight text-white">Statistik</h1>
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500 mt-0.5">
              Mix-Markt
            </div>
          </div>
        </div>

        <nav className="flex items-center gap-0.5 ml-3">
          <NavTab to="/" current={loc.pathname === '/'} icon="dashboard">
            Dashboard
          </NavTab>
          <NavTab to="/pivot" current={loc.pathname === '/pivot'} icon="table">
            Pivot
          </NavTab>
          <ReportsDropdown current={loc.pathname.startsWith('/reports')} />
          <NavTab to="/periodenvergleich" current={loc.pathname === '/periodenvergleich'} icon="trendingUp">
            Vergleich
          </NavTab>
          {user?.role === 'admin' && (
            <NavTab to="/admin" current={loc.pathname === '/admin'} icon="settings">
              Admin
            </NavTab>
          )}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <button
            className="hidden md:flex items-center gap-2 px-3 py-1.5 text-xs text-slate-400 bg-white/5 hover:bg-white/10 hover:text-slate-200 rounded-lg border border-white/10 transition-colors cursor-pointer"
            onClick={() => {
              // Trigger command palette via key event
              document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }));
            }}
            title="Artikel, Filialen, Aktionen suchen (Strg+K)"
          >
            <Icon name="search" size={13} />
            <span>Suchen…</span>
            <kbd className="kbd bg-white/10 border-white/10 text-slate-400">⌘K</kbd>
          </button>
          <button
            className="console-icon-btn"
            onClick={() => setSidebarOpen((s) => !s)}
            title={sidebarOpen ? 'Filter ausblenden' : 'Filter einblenden'}
          >
            <Icon name="panelLeft" size={18} />
          </button>
          <button className="console-icon-btn" onClick={toggleTheme} title="Theme wechseln">
            {dark ? '☀' : '☾'}
          </button>
          <HelpPanel />
          <div className="w-px h-5 bg-slate-700" />
          <AccessBadge />
          <div className="w-px h-5 bg-slate-700" />
          <span className="text-sm text-slate-300">{user?.display_name}</span>
        </div>
      </header>

      {/* Filter Chips */}
      <FilterChips />

      <CommandPalette />

      {/* Body */}
      <div className="flex-1 flex overflow-hidden">
        {sidebarOpen && <FilterSidebar onClose={() => setSidebarOpen(false)} />}
        <main className="flex-1 overflow-auto p-4 lg:p-6 animate-fade-in">
          {children}
        </main>
      </div>
    </div>
  );
}

// Alle Berichte direkt aus der Top-Navigation erreichbar (vorher: nur über
// den Katalog auf /reports — eine Klick-Ebene zu tief vergraben).
const REPORT_LINKS: Array<{ to: string; label: string } | 'sep'> = [
  { to: '/reports/ki-analyse', label: 'KI-Analyse & Prognose' },
  { to: '/reports/umsatz', label: 'Umsatz-Übersicht' },
  { to: '/reports/artikel', label: 'Artikel-Analyse' },
  { to: '/reports/heatmap', label: 'Stoßzeiten-Heatmap' },
  { to: '/reports/wg-trend', label: 'Warengruppen-Trend' },
  { to: '/reports/wg-tagesumsatz', label: 'WG-Tagesumsatz (Matrix)' },
  'sep',
  { to: '/reports/topn', label: 'Top-N-Ranking' },
  { to: '/reports/stornos', label: 'Storno-Analyse' },
  { to: '/reports/benchmark', label: 'Filial-Benchmark' },
  { to: '/reports/aktivitaet', label: 'Marktaktivität' },
  'sep',
  { to: '/reports', label: 'Alle Berichte …' },
];

function ReportsDropdown({ current }: { current: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const loc = useLocation();

  // Bei Navigation und Außenklick schließen
  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`nav-tab flex items-center gap-1.5 cursor-pointer ${
          current ? 'nav-tab-active' : 'nav-tab-inactive'
        }`}
      >
        <Icon name="reports" size={15} />
        Berichte
        <Icon
          name="chevronRight"
          size={11}
          className={`transition-transform ${open ? '-rotate-90' : 'rotate-90'}`}
        />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full mt-1 w-60 py-1.5 bg-white dark:bg-slate-900 rounded-lg shadow-elevated border border-slate-200 dark:border-slate-700 z-40 animate-fade-in"
        >
          {REPORT_LINKS.map((entry, i) =>
            entry === 'sep' ? (
              <div key={`sep-${i}`} className="my-1 border-t border-slate-100 dark:border-slate-800" />
            ) : (
              <Link
                key={entry.to}
                to={entry.to}
                role="menuitem"
                className="block px-3 py-1.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
              >
                {entry.label}
              </Link>
            ),
          )}
        </div>
      )}
    </div>
  );
}

function NavTab({
  to,
  current,
  icon,
  children,
}: {
  to: string;
  current: boolean;
  icon: 'dashboard' | 'table' | 'reports' | 'settings' | 'trendingUp';
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className={`nav-tab flex items-center gap-1.5 ${
        current ? 'nav-tab-active' : 'nav-tab-inactive'
      }`}
    >
      <Icon name={icon} size={15} />
      {children}
    </Link>
  );
}
